import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import {
  BASE_URL,
  AUTH_BASE_URL,
  CLIENT_ID,
  REDIRECT_URI,
} from '../../utils/constants';
import EmailModal from './EmailModal';
import NewPasswordModal from './NewPasswordModal';
import OtpModal from './OtpModal';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [tenant, setTenant] = useState('acme-test-corp-slug');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  // const [showPassword, setShowPassword] = useState(false);

  // Forgot Password States
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [isNewPasswordModalOpen, setIsNewPasswordModalOpen] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [sessionId, setSessionId] = useState('');
  // const [otpExpiresIn, setOtpExpiresIn] = useState(600);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const generatePKCE = async (): Promise<{ codeVerifier: string; codeChallenge: string }> => {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    const codeVerifier = Array.from(array, (byte) => String.fromCharCode(byte))
      .join('')
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 128);

    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);

    const digestArray = new Uint8Array(digest);
    let binary = '';
    for (let i = 0; i < digestArray.length; i += 1) {
      binary += String.fromCharCode(digestArray[i]);
    }

    const codeChallenge = btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    return { codeVerifier, codeChallenge };
  };

  const handleSignIn = async () => {
    if (!tenant.trim()) {
      toast.error('Please enter a tenant');
      return;
    }

    setLoading(true);

    try {
      const { codeVerifier, codeChallenge } = await generatePKCE();

      sessionStorage.setItem('pkce_code_verifier', codeVerifier);
      sessionStorage.setItem('tenant', tenant.trim());
      sessionStorage.setItem('is_new_login', 'true');

      const params = new URLSearchParams({
        response_type: 'code',
        client_id: CLIENT_ID,
        redirect_uri: REDIRECT_URI,
        // redirect_uri: 'http://localhost:3000/callback',
        scope: 'openid profile',
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        state: 'abc123',
        tenant: tenant.trim(),
      });

      const authUrl = `${AUTH_BASE_URL}/oauth2/authorize?${params.toString()}`;

      window.location.replace(authUrl);
    } catch (error) {
      // console.error('Sign in error:', error);
      toast.error('Failed to initiate sign in. Please try again.');
      setLoading(false);
    }
  };

  const handleForgotPasswordClick = () => {
    setIsEmailModalOpen(true);
  };

  const handleEmailSubmit = async (emailAddress: string) => {
    setEmail(emailAddress);
    await initiateForgotPassword(emailAddress);
  };

  const initiateForgotPassword = async (emailAddress: string) => {
    setIsSendingOtp(true);
    try {
      const response = await fetch(`${BASE_URL}/v1/auth/password/forgot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenant.trim(),
        },
        body: JSON.stringify({
          email: emailAddress,
          tenantSlug: tenant.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send OTP');
      }

      const data = await response.json();

      setSessionId(data.sessionId);
      // setOtpExpiresIn(data.otpExpiresInSeconds);

      setIsEmailModalOpen(false);
      setIsOtpModalOpen(true);
      setOtp(['', '', '', '', '', '']);

      startTimer(data.otpExpiresInSeconds);
      toast.success('OTP sent successfully to your email');
    } catch (error: any) {
      // console.error('Forgot password error:', error);
      toast.error(error.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const startTimer = (duration: number) => {
    setTimer(duration);
    setResendDisabled(true);

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleResend = async () => {
    if (!email) {
      toast.error('Email not found. Please try again.');
      return;
    }
    await initiateForgotPassword(email);
  };

  const handleOtpContinue = () => {
    const isOtpValid = otp.every((digit) => digit !== '');
    if (!isOtpValid) {
      toast.error('Please enter complete OTP');
      return;
    }

    setIsOtpModalOpen(false);
    setIsNewPasswordModalOpen(true);
  };

  const handleNewPasswordSubmit = async (newPassword: string) => {
    setIsResettingPassword(true);
    try {
      const otpCode = otp.join('');

      const response = await fetch(`${BASE_URL}/v1/auth/password/reset/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenant.trim(),
        },
        body: JSON.stringify({
          sessionId,
          otp: otpCode,
          newPassword,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reset password');
      }

      toast.success('Password updated successfully! Please login with your new password.');
      setIsNewPasswordModalOpen(false);

      // Clear all states
      setSessionId('');
      setOtp(['', '', '', '', '', '']);
      setEmail('');
      setTimer(0);
      setResendDisabled(true);
    } catch (error: any) {
      // console.error('Password reset error:', error);
      toast.error(error.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleCloseEmailModal = () => {
    if (!isSendingOtp) {
      setIsEmailModalOpen(false);
    }
  };

  const handleCloseOtpModal = () => {
    setIsOtpModalOpen(false);
    setTimer(0);
    setResendDisabled(true);
    setOtp(['', '', '', '', '', '']);
  };

  const handleCloseNewPasswordModal = () => {
    if (!isResettingPassword) {
      setIsNewPasswordModalOpen(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-10">
      <div className="space-y-5">
        <div>
          <label
            className="block text-sm font-medium mb-1"
            style={{ color: '#9FA9B2', fontSize: '12px' }}
          >
            TENANT<span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={tenant}
            onChange={(e) => setTenant(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="e.g. acme-test-corp-slug"
            required
            disabled={loading}
          />
          <p className="text-xs text-gray-500 mt-1">Enter your organisation slug</p>
        </div>

        <button
          onClick={handleSignIn}
          disabled={loading}
          className="w-full text-white py-3 rounded-lg font-medium hover:bg-orange-600 transition-colors disabled:opacity-60"
          style={{ backgroundColor: '#FF7A59' }}
        >
          {loading ? 'Signing in…' : 'Sign In'}
        </button>
      </div>

      <p className="mt-6 text-center text-sm" style={{ color: '#3F5265' }}>
        Don't have an account?{' '}
        <button
          className="text-blue-500 hover:underline"
          style={{ color: '#00A4BD' }}
          onClick={() => navigate('/register')}
        >
          Sign Up
        </button>
      </p>
      <p className="mt-1 text-center text-sm" style={{ color: '#3F5265' }}>
        <button
          className="text-blue-500 hover:underline"
          style={{ color: '#00A4BD' }}
          onClick={handleForgotPasswordClick}
        >
          Forgot password?
        </button>
      </p>

      <div className="flex justify-center gap-6 mt-4">
        {/* Social login buttons can go here */}
      </div>

      {/* Email Modal */}
      <EmailModal
        isOpen={isEmailModalOpen}
        onClose={handleCloseEmailModal}
        onSubmit={handleEmailSubmit}
        tenantSlug={tenant}
        loading={isSendingOtp}
      />

      {/* OTP Modal */}
      {isOtpModalOpen && (
        <OtpModal
          email={email}
          otp={otp}
          timer={timer}
          resendDisabled={resendDisabled}
          isOtpValid={otp.every((digit) => digit !== '')}
          onOtpChange={handleOtpChange}
          onOtpKeyDown={handleOtpKeyDown}
          onResend={handleResend}
          onContinue={handleOtpContinue}
          onClose={handleCloseOtpModal}
          isForgotPassword={true}
        />
      )}

      {/* New Password Modal */}
      {isNewPasswordModalOpen && (
        <NewPasswordModal
          onSubmit={handleNewPasswordSubmit}
          onClose={handleCloseNewPasswordModal}
          loading={isResettingPassword}
        />
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default Login;